import { Component, Vue, Provide, Watch } from 'vue-property-decorator';
import {
    ApiErrorResponse,
    createDtoCustomer,
    CustomerFieldConfig,
    createCustomerFieldConfig,
    DtoMemberType,
    createDtoMemberType,
    CustomerType,
    DtoMemberField,
    DtoUserAccount,
    createDtoUserAccount,
    DtoAddress
} from '@/common/types/AllTypesExports';
import ProfileEdit from '../../components/membership/ProfileEdit.vue';
import { IMemberRegistrationGlobals } from './MemberRegistrationGlobals';
import { customerRepo } from '../../common/repositories/CustomerRepo';
import { AxiosResponse } from 'axios';
import NoviLogging from '../../common/services/NoviLogging';
import NoviStepper from '../../components/stepper/NoviStepper.vue'
import Step, { createStep } from '../../components/stepper/Step';
import ErrorAlert from '../../components/errors/ErrorAlert.vue';
import ValidationErrors from '../../components/errors/ValidationErrors.vue';
import AccountInfo from '../create-account/AccountInfo.vue';
import { DtoCustomer } from '../../common/types/membership/DtoCustomer';
import MembershipCheckout from '../membership-renewal/MembershipCheckout.vue'
import MemberTypeSelection from '../membership-renewal/MemberTypeSelection.vue';
import UrlHelpers from '@/common/services/UrlHelpers';
import { DuplicateCheckResult } from '../../common/types/AllTypesExports';
import MemberRegistrationDupeResult from './MemberRegistrationDupeResult.vue';
import DuplicateChecker from '../../pages/create-account/DuplicateChecker.vue';
import { default as DuplicateCheckerClass } from '@/pages/create-account/DuplicateCheckerVue';
import ValidationService from '../../common/services/ValidationService';
import { default as ProfileEditClass } from '@/components/membership/ProfileEditVue';
import RecaptchaInput from '@/components/inputs/recaptcha/RecaptchaInput.vue';
import RecaptchaInputClass from '../../components/inputs/recaptcha/RecaptchaInputVue';
import { DtoCustomerCreateResponse } from '../../common/types/membership/DtoCustomerCreateResponse';
import EventBus from '../../common/services/EventBus';

declare let noviNs: IMemberRegistrationGlobals;

@Component({
    name: 'MembershipRegistration',
    components: {
        MemberTypeSelection,
        ProfileEdit,
        NoviStepper,
        ErrorAlert,
        ValidationErrors,
        AccountInfo,
        MembershipCheckout,
        MemberRegistrationDupeResult,
        DuplicateChecker,
        RecaptchaInput
    }
})
export default class MembershipRegistration extends Vue {
    userAccount: DtoUserAccount = createDtoUserAccount();
    customer: DtoCustomer = createDtoCustomer();
    parentCustomer: DtoCustomer = createDtoCustomer();
    memberType: DtoMemberType = createDtoMemberType();

    customerFieldConfig: CustomerFieldConfig = createCustomerFieldConfig();
    companyFieldConfig: CustomerFieldConfig = createCustomerFieldConfig();

    errorMessage: string = '';
    hasErrorOnSave: boolean = false;
    isSaving: boolean = false;
    steps: Array<Step> = [];
    currentStep: Step = createStep();
    emailIsAllowed: boolean = false;
    parentCustomerDupeCheckResult: DuplicateCheckResult | null = null;
    parentCustomerDupeCheckLocation: string = "afterName";
    customerDupeCheckResult: DuplicateCheckResult | null = null;
    customerDupeCheckLocation: string = "afterName";
    recaptchaToken: string = '';
    usingExistingParentCustomer = false;
    memberVerificationCode = '';
    userLoggedInOnMemberRegistration: boolean = false;
    secretLink: boolean = false;

    @Provide('validator') $validator: any = this.$validator;

    @Provide('userLoggedInOnMemberRegistration')
    get loginStateProvider(): () => boolean {
        return () => this.userLoggedInOnMemberRegistration;
    }

    /// Computed Properties
    get customerFields() {
        const result: { [key: string]: DtoMemberField } = {};
        this.customerFieldConfig.memberFields.forEach(memberField => {
            if (!memberField) {
                return;
            }
            result[memberField.name.toLowerCase()] = memberField;
        });
        return result;
    }

    get companyFields() {
        const result: { [key: string]: DtoMemberField } = {};
        this.companyFieldConfig.memberFields.forEach(memberField => {
            if (!memberField) {
                return;
            }
            result[memberField.name.toLowerCase()] = memberField;
        });
        return result;
    }

    get parentMemberLabelForPeople(): string { return noviNs.parentMemberLabelForPeople };
    get parentMemberDescriptionForPeople(): string { return noviNs.parentMemberDescriptionForPeople };
    get parentMemberLabelForCompanies(): string { return noviNs.parentMemberLabelForCompanies };
    get parentMemberDescriptionForCompanies(): string { return noviNs.parentMemberDescriptionForCompanies };

    get billingAddressInstructionsForPeople(): string { return noviNs.billingAddressInstructionsForPeople };
    get shippingAddressInstructionsForPeople(): string { return noviNs.shippingAddressInstructionsForPeople };
    get billingAddressInstructionsForCompanies(): string { return noviNs.billingAddressInstructionsForCompanies };
    get shippingAddressInstructionsForCompanies(): string { return noviNs.shippingAddressInstructionsForCompanies };
    get personalAddressInstructions(): string { return noviNs.personalAddressInstructions };
    get professionalEmailInstructions(): string { return noviNs.professionalEmailInstructions };
    get personalEmailInstructions(): string { return noviNs.personalEmailInstructions };
    get emailInstructionsForCompanies(): string { return noviNs.emailInstructionsForCompanies };

    get nextStepText() {
        if (this.currentStep == this.keyInfoStep) {
            if (this.usingExistingParentCustomer) {
                return 'Create Account';
            }
            else return 'Save & Continue';
        }

        return 'Continue';
    }

    get showNext() {
        if (this.currentStep == this.checkoutStep)
        return false;

        return true;
    }

    get disableNext() {
        if (this.currentStep == this.loginInfoStep && !this.emailIsAllowed)
            return true;

        return (this.errors && this.errors.count() > 0) || this.isSaving;
    }

    get duesPayer() {
        if (this.memberType.forCompanies)
            return this.parentCustomer;
        else
            return this.customer;
    }

    get showParentMemberFieldInKeyInfoStep() {
        var parentMemberField = this.customerFieldConfig.memberFields.find(mf => mf.name.toLowerCase() == "parentcustomername");
        if (parentMemberField) {
            return parentMemberField.keyInformation;
        }
        return false;
    }

    private memberTypeStep: Step = {
        name: "membertype",
        title: "Member Type",
        iconClass: "fas fa-handshake",
        completed: false
    };
    private loginInfoStep: Step = {
        name: "login-info",
        title: "Login Info",
        iconClass: "fas fa-user",
        completed: false
    };
    private keyInfoStep: Step = {
        name: "key-info",
        title: "Key Info",
        iconClass: "fas fa-key",
        completed: false
    };
    private checkoutStep: Step = {
        name: "checkout",
        title: "Checkout",
        iconClass: "fas fa-shopping-cart",
        completed: false
    };

    ///Life cycle events

    created() {
        this.customer.customerType = CustomerType.Person;
        this.parentCustomer.customerType = CustomerType.Company;
        
        var memberTypeId = this.getQueryStringMemberTypeId();
        if (memberTypeId) {
            this.loadSingleMemberType(memberTypeId);
            this.currentStep = this.loginInfoStep;
            this.memberTypeStep.completed = true;
        }
        else {
            this.currentStep = this.memberTypeStep;
        }

        this.steps.push(this.memberTypeStep);
        this.steps.push(this.loginInfoStep);
        this.steps.push(this.keyInfoStep);

        EventBus.$on("file-uploaded", this.clearFileUploadErrors);
    }

    //Public Functions
    onUserAccountUpdated() {
        this.customer.firstName = this.userAccount.firstName;
        this.customer.lastName = this.userAccount.lastName;
        this.customer.name = `${this.userAccount.firstName} ${this.userAccount.lastName}`;
        this.customer.email = this.userAccount.email;
    }

    onUserAccountEmailUpdated() {
        this.checkForDuplicateEmail();
    }

    onCustomerParentSelectionUpdated() {
        if (!this.customer.parentCustomerId && this.customer.parentCustomerName)
            this.parentCustomer.name = this.customer.parentCustomerName;
        this.checkForDuplicateCustomer("afterName");
    }

    onParentCustomerBillingAddressUpdated() {
        Object.assign(<DtoAddress>this.customer.parentBilling, this.parentCustomer.billingAddress);
        if (this.customer.useParentBilling) {
            Object.assign(<DtoAddress>this.customer.billingAddress, this.parentCustomer.billingAddress);
        }
        this.checkForDuplicateParentCustomer("afterBillingAddress");
    }
    onParentCustomerShippingAddressUpdated() {
        Object.assign(<DtoAddress>this.customer.parentShipping, this.parentCustomer.shippingAddress);
        if (this.customer.useParentShipping) {
            Object.assign(<DtoAddress>this.customer.shippingAddress, this.parentCustomer.shippingAddress);
        }
        this.checkForDuplicateParentCustomer("afterShippingAddress");
    }
    onParentCustomerNameUpdated() {
        this.checkForDuplicateParentCustomer("afterName");
    }
    onCustomerNameUpdated() {
        this.checkForDuplicateCustomer("afterName");
    }
    onCustomerEmailUpdated() {
        this.checkForDuplicateCustomer("afterEmail");
    }

    checkForDuplicateEmail() {
        let duplicateChecker = <DuplicateCheckerClass>this.$refs.duplicateEmailChecker;
        duplicateChecker.validateEmailAddress()
            //.then((result: void | DuplicateCheckResult | AxiosResponse<ApiErrorResponse>) => { })
            .catch((error: AxiosResponse<ApiErrorResponse>) => { });
    }

    checkForDuplicateParentCustomer(dupeCheckLocation: string) {        
        customerRepo.findDuplicateCompany(
            this.parentCustomer.name,
            this.parentCustomer.billingAddress,
            this.parentCustomer.shippingAddress)
        .then((result : DuplicateCheckResult) => {
            if (result.hasDuplicate) {
                if (this.parentCustomerDupeCheckResult && this.parentCustomerDupeCheckResult.name == result.name)
                    return;

                this.parentCustomerDupeCheckLocation = dupeCheckLocation;
                result.ignored = false;                
            }

            this.parentCustomerDupeCheckResult = result;
        });
    }

    checkForDuplicateCustomer(dupeCheckLocation: string) {
        if (!this.customer.email) {
            this.customerDupeCheckResult = null;
            return;
        }
        this.customerDupeCheckLocation = dupeCheckLocation;
        customerRepo.checkForDuplicate(this.customer.email, this.customer.firstName, this.customer.lastName, this.customer.parentCustomerId)
            .then((result: DuplicateCheckResult) => {
                if (result.hasDuplicate) {
                    if (this.customerDupeCheckResult && this.customerDupeCheckResult.name == result.name)
                        return;

                    this.customerDupeCheckLocation = dupeCheckLocation;
                    result.ignored = false;                    
                }

                this.customerDupeCheckResult = result;
            });
    }

    validateProfiles() {
        let personProfile = <ProfileEditClass>this.$refs.personProfile;
        let companyProfile = <ProfileEditClass>this.$refs.companyProfile;
        let newParentProfile = <ProfileEditClass>this.$refs.newParentProfile;

        if (personProfile) personProfile.validate();
        if (companyProfile) companyProfile.validate();
        if (newParentProfile) newParentProfile.validate();
    }

    saveAndNext() {
        if (this.currentStep == this.keyInfoStep) {
            this.validateProfiles();
        }

        this.$validator.validate()
            .then((valid: any) => {
                if (valid && !this.$validator.errors.any()) {
                    if (this.currentStep == this.keyInfoStep) {
                        if (this.usingExistingParentCustomer) {
                            this.saveUserAccount();
                        }
                        else {
                            this.saveKeyInfo();
                        }
                    }
                    else {
                       this.moveToNextStep();
                    }
                }
                else {
                    NoviLogging.info("Validation Failed");
                    ValidationService.scrollToFirstInvalidField(this.$el);
                }
            });
    }

    setUsingExistingParentCustomer() {
        this.usingExistingParentCustomer = true;
        if (this.parentCustomerDupeCheckResult?.customerId) {
            this.customer.parentCustomerId = this.parentCustomerDupeCheckResult.customerId;
            this.parentCustomer = createDtoCustomer({
                name: this.parentCustomerDupeCheckResult?.name,
                id: this.parentCustomerDupeCheckResult?.customerId,
                billingAddress: this.parentCustomerDupeCheckResult?.addressObject
            });
        }
    }

    //Private Functions
    private moveToNextStep() {
        this.currentStep.completed = true;

        if (this.currentStep == this.keyInfoStep && this.memberType.requireApplication) {
            window.location.href = `/confirm-email?userId=${this.duesPayer.userId}&checkoutType=new&memberTypeId=${this.memberType.id}`;
        }
        else {
            var nextStep = this.steps[this.steps.indexOf(this.currentStep) + 1];
            this.currentStep = nextStep;
            window.scrollTo({ top: (<HTMLElement>this.$refs.container).offsetTop });
        }
    }

    private saveKeyInfo() {
        this.isSaving = true;
        var parentCustomer = this.memberType.forCompanies || this.customer.parentCustomerId == 0 ? this.parentCustomer : undefined;
        customerRepo.createNewMember(this.customer, this.userAccount, this.secretLink, parentCustomer, this.recaptchaToken).then((savedCustomer) => {
            this.duesPayer.id = savedCustomer.id;
            this.duesPayer.userId = savedCustomer.userId;
            this.duesPayer.defaultBillablePartyId = savedCustomer.defaultBillablePartyId;
            this.moveToNextStep();
            this.isSaving = false;
            this.hasErrorOnSave = false;
            this.userLoggedInOnMemberRegistration = true;
        })
        .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
            this.handleSaveUserAccountError(axiosResponse);
        });
    }

    private saveUserAccount() {
        this.isSaving = true;

        if (this.parentCustomerDupeCheckResult?.customerId) {
            this.customer.parentCustomerId = this.parentCustomerDupeCheckResult.customerId;
        }

        customerRepo.createUserAccount(this.customer, this.userAccount, this.memberVerificationCode, this.parentCustomer, this.recaptchaToken)
            .then((result: DtoCustomerCreateResponse) => {
                if (result.isAutoLoggedIn) {
                    window.location.href = `/member-compass`;
                } else {
                    window.location.href = `/confirm-email?userId=` + result.userId;
                }
            })
            .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                this.handleSaveUserAccountError(axiosResponse);
            });
    }

    private handleSaveUserAccountError(axiosResponse: AxiosResponse<ApiErrorResponse>) {
        this.isSaving = false;
        this.keyInfoStep.completed = false;
        var customerType = this.customer.customerType || CustomerType.Person;
        this.errors.clear(customerType);

        let validationErrors = axiosResponse?.data?.links || {};
        const hasValidationErrors = Object.keys(validationErrors).length > 0;

        let recaptchaInput = <RecaptchaInputClass>this.$refs.recaptcha;
        if (recaptchaInput) {
            recaptchaInput.reset();
        }

        if (!hasValidationErrors) {
            this.errorMessage = `Could not create your account: ${axiosResponse?.data?.message || 'Unknown error'}`;
            this.hasErrorOnSave = true;
            return;
        }

        this.errorMessage = "";
        this.hasErrorOnSave = false;

        /*
         *  The validation errors returned contains errors for both the individual and the company
         *  We need to separate them so that we can add the errors to the appropriate scope
         */

        // Individual error keys look like "request.customer.phone"
        var individualValidationErrors = Object.keys(validationErrors)
            .filter(error => !error.includes("parent"))
            .reduce((obj: any, key) => {
                obj[key] = validationErrors[key];
                return obj;
            }, {});

        // Company error keys look like "request.parentcustomer.phone"
        var companyValidationErrors = Object.keys(validationErrors)
            .filter(error => error.includes("parent"))
            .reduce((obj: any, key) => {
                obj[key] = validationErrors[key];
                return obj;
            }, {});
        
        ValidationService.parseValidationErrors(individualValidationErrors, CustomerType.Person, this.customerFields, this.$validator);
        ValidationService.parseValidationErrors(companyValidationErrors, CustomerType.Company, this.companyFields, this.$validator);
        ValidationService.scrollToFirstInvalidField(this.$el);
    }

    @Watch("memberType")
    onMemberTypeChanged() {
        if (this.memberType.forCompanies) {
            this.parentCustomer.memberType = this.memberType;
            this.customer.memberType = null;
        }
        else {
            this.customer.memberType = this.memberType;
            this.parentCustomer.memberType = null;
        }
        this.getFieldConfig();
    }

    onProfileEditMounted() {
        var firstInput = this.$el.querySelector("input");
        if (firstInput) {
            (<HTMLElement>firstInput).focus();
        }

        if (!this.showParentMemberFieldInKeyInfoStep) {
            this.checkForDuplicateCustomer("afterName");
        }
    }

    clearFileUploadErrors() {
        this.errors.clear(CustomerType.Person);
    }

    private getFieldConfig() {
        //this.isLoading_FieldConfig = true;
        var personFieldConfigPromise = customerRepo.getFieldConfiguration(false, true, this.memberType.id)
            .then((fieldConfig) => {
                this.customerFieldConfig = Object.assign(this.customerFieldConfig, fieldConfig);
                return this.customerFieldConfig;
            })
            .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                this.errorMessage = `Unable to load the field configuration information. ${axiosResponse.data.message}`;
                throw axiosResponse;
            });
        var companyFieldConfigPromise = customerRepo.getFieldConfiguration(true, true, this.memberType.id)
            .then((fieldConfig) => {
                this.companyFieldConfig = Object.assign(this.companyFieldConfig, fieldConfig);
                return this.companyFieldConfig;
            })
            .catch((axiosResponse: AxiosResponse<ApiErrorResponse>) => {
                this.errorMessage = `Unable to load the field configuration information. ${axiosResponse.data.message}`;
                throw axiosResponse;
            });
        //.finally(() => this.isLoading_FieldConfig = false);
    }

    private getQueryStringMemberTypeId() : number | null {
        let query = UrlHelpers.getUrlParameter('memberTypeId');
        if (!query) {
            return null;
        }

        return parseInt(query);
    }

    loadSingleMemberType(memberTypeId: number) {

        let isSecretLinkString = UrlHelpers.getUrlParameter('isSecretLink').toLowerCase();
        this.secretLink = isSecretLinkString === 'true';

        if (this.secretLink) {
            customerRepo.getSecretMemberTypes(memberTypeId)
                .then(result => {
                    this.memberType = result[0];
                    if (this.memberType && !this.memberType.requireApplication) {
                        this.steps.push(this.checkoutStep);
                    }
                })
                .catch((message) => {
                    this.errorMessage = message;
                });
            //.finally(() => this.isLoading = false);
        }
        else {
            customerRepo.getMemberTypes(memberTypeId)
                .then(result => {
                    this.memberType = result[0];
                    if (this.memberType && !this.memberType.requireApplication) {
                        this.steps.push(this.checkoutStep);
                    }
                })
                .catch((message) => {
                    this.errorMessage = message;
                });
                //.finally(() => this.isLoading = false);
        }
    }
}